import React, {useEffect, useMemo, useState} from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import {DeleteOutline, ExpandMore, HistoryToggleOff, CheckCircleOutline, CancelOutlined} from '@mui/icons-material';
import {deleteForecastJob, getForecastHistory} from '../../../../../utils/api/aiCitationAnalysis';
import {buildForecastSourceIndex, OpinionEvidenceText, PersonaReferencesList} from './ForecastCitations';
import {ForecastSourcesList} from './ForecastSourcesList';
import {ForecastPreviewText} from './ForecastPreview';
import Timestamp from '../../../../../components/Timestamp';
import UserAvatar from '../../../../../components/UserAvatar';
import {formatDate, formatTime} from '../../../../../utils/formatTimestamp';

// Always-absolute date+time ("August 17, 2026, 8:50 PM") for the delete-confirmation dialog —
// unlike the row's <Timestamp>, this text reads as "generated on {value}" and must not resolve
// to relative phrasing like "2h ago", which doesn't pair grammatically with "on".
function formatAbsoluteJobDate(value) {
    const datePart = formatDate(value, null);
    if (!datePart) return null;
    return `${datePart}, ${formatTime(value, '')}`.trim().replace(/,\s*$/, '');
}

const HORIZON_LABELS = {
    '1_MONTH': '1 month',
    '3_MONTHS': '3 months',
    '6_MONTHS': '6 months',
    '1_YEAR': '1 year',
};

const CREATIVITY_LABELS = ['Cautious', 'Conservative', 'Exploratory', 'Speculative'];

/**
 * Confirmation dialog for deleting a single forecast history entry — mirrors
 * `DeleteReportDialog.jsx`'s structure/wording, scoped to one job instead of a whole report.
 */
function DeleteForecastJobDialog({open, onClose, onConfirm, deleting, error, jobDate}) {
    return (
        <Dialog open={open} onClose={deleting ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Delete this forecast?</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Typography variant="body1">
                        This will permanently delete the forecast{jobDate ? ` generated on ${jobDate}` : ''}.
                    </Typography>
                    <Alert severity="warning">This cannot be undone.</Alert>
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={deleting}>
                    Cancel
                </Button>
                <Button color="error" onClick={onConfirm} disabled={deleting}>
                    {deleting ? 'Deleting…' : 'Delete'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Forecast History View: one past forecast job — an expandable row showing the generation
 * context it was retained with (horizon, analysis depth, personas, instruction) as chips in the
 * summary, and the full result (reusing the same `ForecastPreviewText`/`ForecastSourcesList`
 * rendering `Forecast.jsx` uses for the live result) in the details. When `isOwner` is true, a
 * delete icon lets the report owner remove this entry (`onDeleteRequest`).
 */
function ForecastHistoryItem({job, isOwner, onDeleteRequest, availablePersonas}) {
    const forecast = job.result;
    const context = job.requestContext || {};
    const {sourcesByKey, numberByKey} = useMemo(() => buildForecastSourceIndex(forecast), [forecast]);

    const horizonLabel = HORIZON_LABELS[context.horizon] || context.horizon || null;
    const creativityLabel = typeof context.creativity === 'number' ? CREATIVITY_LABELS[context.creativity] : null;
    const personaHandles = Array.isArray(context.personaHandles) ? context.personaHandles : [];

    // Report version number: prefer the first-class field on the job response; fall back to the
    // value inside requestContext for jobs created with an older API that didn't expose it directly.
    const reportVersionNumber = job.reportVersionNumber ?? context.reportVersionNumber ?? null;

    // Handles are all `requestContext` retains — look up each expert's display name + avatar
    // from the live experts roster so the row reads with a real name/photo instead of a raw handle.
    const personaProfileByHandle = useMemo(() => {
        const map = new Map();
        (availablePersonas || []).forEach((p) => {
            if (p?.handle) map.set(p.handle, p);
        });
        return map;
    }, [availablePersonas]);

    return (
        <Accordion disableGutters variant="outlined">
            <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
                <AccordionSummary
                    expandIcon={<ExpandMore/>}
                    sx={{
                        flexGrow: 1,
                        minWidth: 0,
                        alignItems: 'flex-start',
                        '& .MuiAccordionSummary-expandIconWrapper': {mt: 1},
                    }}
                >
                    <Stack spacing={0.75} sx={{width: '100%', pr: 1}}>
                        <Typography
                            variant="body1"
                            fontWeight={700}
                            color={context.userInstructions ? 'text.primary' : 'text.secondary'}
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {context.userInstructions ? `“${context.userInstructions}”` : 'General forecast (no question specified)'}
                        </Typography>
                        {(horizonLabel || creativityLabel || personaHandles.length > 0 || reportVersionNumber != null) && (
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
                                {reportVersionNumber != null && (
                                    <Tooltip title="Report version this forecast was produced from">
                                        <Chip label={`Report v${reportVersionNumber}`} size="small" variant="outlined"/>
                                    </Tooltip>
                                )}
                                {horizonLabel && (
                                    <Chip label={`Horizon: ${horizonLabel}`} size="small" variant="outlined"/>
                                )}
                                {creativityLabel && (
                                    <Chip label={`Analysis depth: ${creativityLabel}`} size="small" variant="outlined"/>
                                )}
                                {personaHandles.length > 0 && (
                                    <>
                                        <Typography variant="caption" color="text.secondary">
                                            Personas:
                                        </Typography>
                                        {personaHandles.map((handle) => {
                                            const profile = personaProfileByHandle.get(handle);
                                            const displayName = profile?.displayName || handle;
                                            return (
                                                <Chip
                                                    key={handle}
                                                    avatar={
                                                        <UserAvatar
                                                            user={profile || {handle, displayName}}
                                                            sx={{width: 20, height: 20, fontSize: 10}}
                                                        />
                                                    }
                                                    label={displayName}
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                            );
                                        })}
                                    </>
                                )}
                            </Stack>
                        )}
                        <Typography variant="caption" color="text.secondary">
                            <Timestamp value={job.completedAt || job.createdAt} fallback=""/>
                        </Typography>
                    </Stack>
                </AccordionSummary>
                {isOwner && (
                    <Tooltip title="Delete this forecast">
                        <IconButton
                            aria-label="Delete forecast"
                            size="small"
                            sx={{mt: 1, mr: 2}}
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteRequest(job);
                            }}
                        >
                            <DeleteOutline fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            <AccordionDetails sx={{pt: 0}}>
                {!forecast ? (
                    <Typography variant="body2" color="text.secondary">
                        No result available for this job.
                    </Typography>
                ) : (
                    <Stack spacing={2}>
                        {forecast.summary && (
                            <ForecastPreviewText
                                text={forecast.summary}
                                sourcesByKey={sourcesByKey}
                                numberByKey={numberByKey}
                            />
                        )}

                        {forecast.confidence && (
                            <Chip
                                label={`Overall confidence: ${forecast.confidence}`}
                                size="small"
                                sx={{alignSelf: 'flex-start'}}
                            />
                        )}

                        {forecast.keyDrivers?.length > 0 && (
                            <Stack spacing={0.5}>
                                <Typography variant="subtitle2" color="primary">
                                    Key Drivers
                                </Typography>
                                {forecast.keyDrivers.map((driver, idx) => (
                                    <Stack key={idx} direction="row" spacing={0.5}>
                                        <Typography variant="body2">•</Typography>
                                        <ForecastPreviewText
                                            text={driver}
                                            sourcesByKey={sourcesByKey}
                                            numberByKey={numberByKey}
                                        />
                                    </Stack>
                                ))}
                            </Stack>
                        )}

                        {forecast.predictions?.length > 0 && (
                            <Stack spacing={2}>
                                <Typography variant="subtitle2" color="primary">
                                    Predictions
                                </Typography>
                                {forecast.predictions.map((pred, idx) => (
                                    <Stack key={idx} spacing={1} alignItems="flex-start">
                                        <ForecastPreviewText
                                            text={`${idx + 1}. ${pred.statement}`}
                                            citedSources={pred.citedSources}
                                            sourcesByKey={sourcesByKey}
                                            numberByKey={numberByKey}
                                            bold
                                        />
                                        {pred.rationale && (
                                            <Typography variant="body2" color="text.secondary">
                                                {pred.rationale}
                                            </Typography>
                                        )}
                                        {pred.citedSources?.length > 0 && (
                                            <ForecastSourcesList
                                                citedSources={pred.citedSources}
                                                sourcesByKey={sourcesByKey}
                                                numberByKey={numberByKey}
                                            />
                                        )}
                                        {idx < forecast.predictions.length - 1 && <Divider sx={{width: '100%'}}/>}
                                    </Stack>
                                ))}
                            </Stack>
                        )}

                        {forecast.personaOpinions?.length > 0 && (
                            <Stack spacing={1.5}>
                                <Divider/>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    Expert Opinions
                                </Typography>
                                {forecast.personaOpinions.map((op, idx) => {
                                    const profile = personaProfileByHandle.get(op.handle);
                                    const hasPoints = (op.agreements?.length > 0) || (op.disagreements?.length > 0);
                                    return (
                                        <Stack key={op.handle || idx} spacing={0.75}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <UserAvatar
                                                    user={profile || {handle: op.handle, displayName: op.displayName}}
                                                    sx={{width: 24, height: 24, fontSize: 12}}
                                                />
                                                <Typography variant="subtitle2">
                                                    {profile?.displayName || op.displayName || op.handle}
                                                </Typography>
                                                {op.confidence != null && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ml: 'auto'}}>
                                                        {typeof op.confidence === 'number'
                                                            ? `${Math.round(op.confidence * 100)}% confidence`
                                                            : `Confidence: ${op.confidence}`}
                                                    </Typography>
                                                )}
                                            </Stack>

                                            {hasPoints ? (
                                                <Stack spacing={0.5} sx={{pl: 1}}>
                                                    {op.agreements?.map((pt, i) => (
                                                        <Stack key={i} spacing={0.25}>
                                                            <Stack direction="row" spacing={0.75} alignItems="flex-start">
                                                                <CheckCircleOutline sx={{fontSize: 15, color: 'success.main', mt: '4px', flexShrink: 0}}/>
                                                                <ForecastPreviewText
                                                                    text={pt.point}
                                                                    sourcesByKey={sourcesByKey}
                                                                    numberByKey={numberByKey}
                                                                />
                                                            </Stack>
                                                            {pt.evidence && (
                                                                <OpinionEvidenceText evidence={pt.evidence}/>
                                                            )}
                                                        </Stack>
                                                    ))}
                                                    {op.disagreements?.map((pt, i) => (
                                                        <Stack key={i} spacing={0.25}>
                                                            <Stack direction="row" spacing={0.75} alignItems="flex-start">
                                                                <CancelOutlined sx={{fontSize: 15, color: 'warning.main', mt: '4px', flexShrink: 0}}/>
                                                                <ForecastPreviewText
                                                                    text={pt.point}
                                                                    sourcesByKey={sourcesByKey}
                                                                    numberByKey={numberByKey}
                                                                />
                                                            </Stack>
                                                            {pt.evidence && (
                                                                <OpinionEvidenceText evidence={pt.evidence}/>
                                                            )}
                                                        </Stack>
                                                    ))}
                                                    <PersonaReferencesList points={[...(op.agreements || []), ...(op.disagreements || [])]}/>
                                                </Stack>
                                            ) : op.opinion ? (
                                                <Typography variant="body2" sx={{pl: 1}}>{op.opinion}</Typography>
                                            ) : null}
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        )}
                    </Stack>
                )}
            </AccordionDetails>
        </Accordion>
    );
}

/**
 * Forecast History View: the "History" tab of the Forecast panel — every completed forecast for
 * this report, newest first, each retaining the generation context it was produced with.
 * When `isOwner` is true, each row also gets a delete affordance (owner-only, enforced server-side
 * too) that removes it optimistically from the list once confirmed. `availablePersonas` (the live
 * experts roster, already loaded by the parent `Forecast.jsx`) is used to resolve each retained
 * persona handle to a real display name + avatar instead of a raw handle string.
 */
const ForecastHistory = ({reportId, isOwner = false, availablePersonas = []}) => {
    const [jobs, setJobs] = useState(null); // null = loading
    const [error, setError] = useState(null);
    const [jobPendingDelete, setJobPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    useEffect(() => {
        if (!reportId) return undefined;
        let cancelled = false;
        setJobs(null);
        setError(null);

        getForecastHistory(reportId)
            .then((data) => {
                if (!cancelled) setJobs(data);
            })
            .catch((err) => {
                if (!cancelled) setError(err);
            });

        return () => {
            cancelled = true;
        };
    }, [reportId]);

    const closeDeleteDialog = () => {
        if (deleting) return;
        setJobPendingDelete(null);
        setDeleteError(null);
    };

    const handleConfirmDelete = async () => {
        if (!jobPendingDelete) return;
        setDeleting(true);
        setDeleteError(null);
        try {
            await deleteForecastJob(jobPendingDelete.id);
            setJobs((prev) => (prev || []).filter((j) => j.id !== jobPendingDelete.id));
            setJobPendingDelete(null);
        } catch (err) {
            console.error('❌ Failed to delete forecast job:', err);
            setDeleteError('Failed to delete this forecast. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    if (jobs === null && !error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress size={24}/>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{m: 2}}>
                Failed to load forecast history.
            </Alert>
        );
    }

    if (jobs.length === 0) {
        return (
            <Stack alignItems="center" spacing={1} p={4} color="text.secondary">
                <HistoryToggleOff/>
                <Typography variant="body2">No forecasts generated yet.</Typography>
            </Stack>
        );
    }

    return (
        <>
            <Stack p={2} spacing={1}>
                {jobs.map((job) => (
                    <ForecastHistoryItem
                        key={job.id}
                        job={job}
                        isOwner={isOwner}
                        onDeleteRequest={setJobPendingDelete}
                        availablePersonas={availablePersonas}
                    />
                ))}
            </Stack>
            <DeleteForecastJobDialog
                open={Boolean(jobPendingDelete)}
                onClose={closeDeleteDialog}
                onConfirm={handleConfirmDelete}
                deleting={deleting}
                error={deleteError}
                jobDate={jobPendingDelete ? formatAbsoluteJobDate(jobPendingDelete.completedAt || jobPendingDelete.createdAt) : null}
            />
        </>
    );
};

export default ForecastHistory;
